const { h1, h2, p, kicker, ul, img, note, info, warn, safe, table, rich, page } = require("./blocks");

const PATHS = {
  en: "/aviator-ai-forecast-models-explained",
  es: "/es/modelos-prevision-ia-aviator",
  "pt-BR": "/pt-br/modelos-previsao-ia-aviator",
  "pt-PT": "/pt-pt/modelos-previsao-ia-aviator",
  de: "/de/aviator-ki-prognose-modelle",
  it: "/it/modelli-previsione-ia-aviator",
};

const copy = {
  en: page(
    "Aviator AI Forecast: Models and Accuracy Explained | AviatorTrends",
    "How AviatorTrends sliding-window, EWMA and decay-Dirichlet models work, what Stop-Loss and Win Accuracy count, and how to read an 80% row.",
    [
      kicker("Ranges, windows and denominators"),
      h1("Inside the AviatorTrends Live AI Forecast"),
      p("“AI predictor” is easy to advertise and hard to define. AviatorTrends shows several models, several windows, forecast ranges, historical hit rates and sample counts. That is more information. It needs a short explanation."),
      img("Aviator AIForecast", "AviatorTrends Live AI Forecast panel", "Ranges, windows and sample counts — not an exact next multiplier."),
      h2("A range, not an exact multiplier"),
      p("Crash data are skewed: many low results and a few very large ones. A screenshot of 7.42× looks sharp and still fails over a full history. AviatorTrends places multipliers into ranges so a forecast can be tested."),
      h2("Three models, different memory"),
      ul([
        "Sliding window: counts what happened in the last 30, 100 or similar rounds. Fast, noisy.",
        "EWMA: updates a probability estimate over chronological chunks. Recent data moves it; older data is not thrown away.",
        "Decay-Dirichlet: older counts fade, and a prior keeps empty bins from being treated as impossible.",
      ]),
      p("A short window reacts quickly. A long window is calmer. If window 30 and window 300 disagree, that disagreement is the signal."),
      table([
        ["Stop-Loss Accuracy", "Actual range was equal to or above the predicted range in walk-forward tests"],
        ["Win Accuracy", "Actual multiplier reached the lower edge of the predicted range, on qualifying rows"],
        ["Qualifying sample", "At least 30 qualifying predictions before a row ranks in Top Historical Win Accuracy"],
      ]),
      h2("Walk-forward testing"),
      p("For each historical position the model trains only on earlier observations. The value being scored is not in its training window. That is how the percentages are built."),
      warn("How to read 24/30 — 80%", "It means the lower boundary was reached in 24 of 30 qualifying historical checks. It does not mean the next round is 80% certain, that the exact multiplier was predicted, or that a strategy would have been profitable.", [
        "Check the predicted range next to the percentage",
        "An 80% at a low threshold is easier than 80% at a high threshold",
        "Prefer rows with larger denominators",
      ]),
      note("Live means the predictions socket delivered the current snapshot or a streamed update. Cached is the HTTP fallback until that payload arrives."),
      info("What Extended Data Access unlocks", "Paid access reveals forecast ranges and next-five sequences while keeping the historical metrics needed to judge them.", [
        "Live model updates",
        "Model agreement and threshold chances",
        "Stop-Loss and Win Accuracy with counts",
        "Larger context windows",
      ]),
      safe("Use the model as a filter", "Several models can agree and still be wrong because they share the same observable history, not the hidden seed. If they disagree, waiting is a valid decision.", []),
      rich("Open the Live AI Forecast tab on ", "/", "AviatorTrends", "."),
    ]
  ),
  es: page(
    "Previsión IA de Aviator: modelos y precisión | AviatorTrends",
    "Cómo funcionan la ventana deslizante, EWMA y decay-Dirichlet, qué cuentan Stop-Loss y Win Accuracy, y cómo leer un 80%.",
    [
      kicker("Rangos, ventanas y denominadores"),
      h1("Dentro de la previsión IA en vivo de AviatorTrends"),
      p("AviatorTrends muestra varios modelos, ventanas, rangos, tasas históricas y recuentos. Eso pide una explicación breve."),
      img("Aviator AIForecast", "Panel de previsión IA en vivo de AviatorTrends", "Rangos, ventanas y recuentos — no el siguiente multiplicador exacto."),
      h2("Un rango, no un multiplicador exacto"),
      p("Hay muchos resultados bajos y pocos muy altos. Un 7,42× en una captura no basta. Los rangos se pueden comprobar."),
      h2("Tres modelos, distinta memoria"),
      ul([
        "Ventana deslizante: cuenta lo reciente. Rápida y ruidosa.",
        "EWMA: actualiza la probabilidad por bloques. Lo nuevo mueve; lo viejo no se tira.",
        "Decay-Dirichlet: lo antiguo se desvanece y un prior evita tratar un bin vacío como imposible.",
      ]),
      table([
        ["Precisión stop-loss", "El rango real fue igual o superior al previsto en pruebas walk-forward"],
        ["Precisión de ganancia", "El multiplicador real alcanzó el borde inferior del rango, en filas cualificadas"],
        ["Muestra mínima", "Al menos 30 previsiones cualificadas para el ranking"],
      ]),
      warn("Cómo leer 24/30 — 80%", "El borde inferior se alcanzó en 24 de 30 pruebas históricas cualificadas. No es un 80% para la siguiente ronda.", []),
      note("Live es el socket de previsiones. Cached es el respaldo HTTP hasta el primer payload."),
      info("Qué desbloquea el acceso extendido", "Rangos, siguientes cinco, acuerdo de modelos y métricas históricas con recuento.", []),
      safe("Usa el modelo como filtro", "Varios modelos pueden coincidir y fallar: comparten el mismo historial observable, no la semilla oculta.", []),
      rich("Abre la pestaña Live AI Forecast en ", "/", "AviatorTrends", "."),
    ]
  ),
  "pt-BR": page(
    "Previsão IA do Aviator: modelos e precisão | AviatorTrends",
    "Como funcionam janela deslizante, EWMA e decay-Dirichlet, o que Stop-Loss e Win Accuracy contam, e como ler um 80%.",
    [
      kicker("Intervalos, janelas e denominadores"),
      h1("Dentro da previsão IA ao vivo da AviatorTrends"),
      p("A AviatorTrends mostra vários modelos, janelas, intervalos, taxas históricas e contagens. Isso pede uma explicação curta."),
      img("Aviator AIForecast", "Painel de previsão IA ao vivo da AviatorTrends", "Intervalos, janelas e contagens — não o próximo multiplicador exato."),
      h2("Um intervalo, não um multiplicador exato"),
      p("Há muitos resultados baixos e poucos muito altos. Um 7,42× numa captura não basta."),
      h2("Três modelos, memórias diferentes"),
      ul([
        "Janela deslizante: conta o recente. Rápida e ruidosa.",
        "EWMA: atualiza a probabilidade em blocos. O novo empurra; o antigo não some.",
        "Decay-Dirichlet: o antigo esvanece e um prior evita tratar faixa vazia como impossível.",
      ]),
      table([
        ["Stop-Loss Accuracy", "A faixa real foi igual ou acima da prevista nos testes walk-forward"],
        ["Win Accuracy", "O multiplicador real alcançou a borda inferior, em linhas qualificadas"],
        ["Amostra mínima", "Pelo menos 30 previsões qualificadas no ranking"],
      ]),
      warn("Como ler 24/30 — 80%", "A borda inferior foi alcançada em 24 de 30 checagens históricas qualificadas. Não é 80% na próxima rodada.", []),
      note("Live é o socket de previsões. Cached é o HTTP até o primeiro payload."),
      info("O que o acesso estendido libera", "Intervalos, próximos cinco, acordo dos modelos e métricas com contagem.", []),
      safe("Use o modelo como filtro", "Vários modelos podem concordar e errar: compartilham o mesmo histórico observável.", []),
      rich("Abra a aba Live AI Forecast em ", "/", "AviatorTrends", "."),
    ]
  ),
  "pt-PT": page(
    "Previsão IA do Aviator: modelos e precisão | AviatorTrends",
    "Como funcionam janela deslizante, EWMA e decay-Dirichlet, o que Stop-Loss e Win Accuracy contam, e como ler um 80%.",
    [
      kicker("Intervalos, janelas e denominadores"),
      h1("Dentro da previsão IA em directo da AviatorTrends"),
      p("A AviatorTrends mostra vários modelos, janelas, intervalos, taxas históricas e contagens."),
      img("Aviator AIForecast", "Painel de previsão IA em directo da AviatorTrends", "Intervalos, janelas e contagens — não o multiplicador seguinte exacto."),
      h2("Um intervalo, não um multiplicador exacto"),
      p("Há muitos resultados baixos e poucos muito altos. Um 7,42× numa captura não chega."),
      h2("Três modelos, memórias diferentes"),
      ul([
        "Janela deslizante: conta o recente. Rápida e ruidosa.",
        "EWMA: actualiza a probabilidade em blocos.",
        "Decay-Dirichlet: o antigo esvanece e um prior evita tratar um intervalo vazio como impossível.",
      ]),
      table([
        ["Stop-Loss Accuracy", "O intervalo real foi igual ou acima do previsto nos testes walk-forward"],
        ["Win Accuracy", "O multiplicador real atingiu o limite inferior, em linhas qualificadas"],
        ["Amostra mínima", "Pelo menos 30 previsões qualificadas no ranking"],
      ]),
      warn("Como ler 24/30 — 80%", "O limite inferior foi atingido em 24 de 30 verificações históricas qualificadas. Não é 80% na ronda seguinte.", []),
      info("O que o acesso alargado liberta", "Intervalos, próximos cinco, acordo dos modelos e métricas com contagem.", []),
      safe("Use o modelo como filtro", "Vários modelos podem concordar e falhar: partilham o mesmo histórico observável.", []),
      rich("Abra o separador Live AI Forecast em ", "/", "AviatorTrends", "."),
    ]
  ),
  de: page(
    "Aviator-KI-Prognose: Modelle und Trefferquoten | AviatorTrends",
    "Wie Sliding-Window, EWMA und Decay-Dirichlet arbeiten, was Stop-Loss- und Win-Accuracy zählen und wie eine 80%-Zeile zu lesen ist.",
    [
      kicker("Bereiche, Fenster und Nenner"),
      h1("Die Live-KI-Prognose von AviatorTrends"),
      p("AviatorTrends zeigt mehrere Modelle, Fenster, Bereiche, historische Trefferquoten und Stichprobenzahlen."),
      img("Aviator AIForecast", "Live-KI-Prognose von AviatorTrends", "Bereiche, Fenster und Zähler — nicht der nächste exakte Multiplikator."),
      h2("Ein Bereich, kein exakter Multiplikator"),
      p("Viele niedrige Werte, wenige sehr hohe. Ein 7,42× im Screenshot reicht nicht."),
      h2("Drei Modelle, unterschiedliches Gedächtnis"),
      ul([
        "Sliding Window: zählt das Aktuelle. Schnell und unruhig.",
        "EWMA: aktualisiert die Schätzung in Blöcken. Neues verschiebt, Altes bleibt gewichtet.",
        "Decay-Dirichlet: Altes klingt ab, ein Prior verhindert, dass leere Intervalle unmöglich wirken.",
      ]),
      table([
        ["Stop-Loss Accuracy", "Tatsächlicher Bereich gleich oder über dem prognostizierten, in Walk-Forward-Tests"],
        ["Win Accuracy", "Tatsächlicher Multiplikator erreichte die Untergrenze, bei qualifizierenden Zeilen"],
        ["Mindeststichprobe", "Mindestens 30 qualifizierende Prognosen im Ranking"],
      ]),
      warn("24/30 — 80% lesen", "Die Untergrenze wurde in 24 von 30 geeigneten historischen Tests erreicht. Das ist keine 80%-Chance für die nächste Runde.", []),
      info("Was der erweiterte Zugang öffnet", "Bereiche, nächste fünf, Modellübereinstimmung und Kennzahlen mit Zähler.", []),
      safe("Modell als Filter nutzen", "Mehrere Modelle können übereinstimmen und trotzdem falsch liegen.", []),
      rich("Öffnen Sie den Tab Live AI Forecast auf ", "/", "AviatorTrends", "."),
    ]
  ),
  it: page(
    "Previsione IA di Aviator: modelli e precisione | AviatorTrends",
    "Come funzionano finestra mobile, EWMA e decay-Dirichlet, cosa contano Stop-Loss e Win Accuracy e come leggere un 80%.",
    [
      kicker("Intervalli, finestre e denominatori"),
      h1("Dentro la previsione IA live di AviatorTrends"),
      p("AviatorTrends mostra più modelli, finestre, intervalli, tassi storici e conteggi."),
      img("Aviator AIForecast", "Pannello Live AI Forecast di AviatorTrends", "Intervalli, finestre e conteggi — non il moltiplicatore esatto successivo."),
      h2("Un intervallo, non un moltiplicatore esatto"),
      p("Molti risultati bassi e pochi molto alti. Un 7,42× nello screenshot non basta."),
      h2("Tre modelli, memorie diverse"),
      ul([
        "Finestra mobile: conta il recente. Veloce e rumorosa.",
        "EWMA: aggiorna la probabilità a blocchi. Il nuovo sposta, il vecchio non sparisce.",
        "Decay-Dirichlet: il vecchio svanisce e un prior evita di trattare un intervallo vuoto come impossibile.",
      ]),
      table([
        ["Stop-Loss Accuracy", "L'intervallo reale era uguale o sopra quello previsto nei test walk-forward"],
        ["Win Accuracy", "Il moltiplicatore reale ha raggiunto il bordo inferiore, sulle righe qualificate"],
        ["Campione minimo", "Almeno 30 previsioni qualificate in classifica"],
      ]),
      warn("Come leggere 24/30 — 80%", "Il bordo inferiore è stato raggiunto in 24 di 30 controlli storici qualificanti. Non è l'80% sul round successivo.", []),
      info("Cosa sblocca l'accesso esteso", "Intervalli, prossimi cinque, accordo dei modelli e metriche con conteggio.", []),
      safe("Usa il modello come filtro", "Più modelli possono essere d'accordo e sbagliare comunque.", []),
      rich("Apri la scheda Live AI Forecast su ", "/", "AviatorTrends", "."),
    ]
  ),
};

module.exports = {
  id: "models",
  paths: PATHS,
  copy,
  hero: "Aviator AIForecast",
  related: ["predict", "dashboard", "history", "forecast"],
};
